// File: src/components/chat/ChatInputBox/ChatInputBox.tsx
// Purpose: Chat-related UI component

import React, { useState } from "react";
import styled from "styled-components";
import { Send } from "lucide-react";

interface ChatInputBoxProps {
  value: string;
  onChange: (text: string) => void;
  onSend: (text: string) => void;
}

const Container = styled.div`
  display: flex;
  align-items: center;
  padding: ${({ theme }) => theme.spacing.md};
  background-color: ${({ theme }) => theme.colors.surface};
  border-top: 1px solid ${({ theme }) => theme.colors.border};
`;

const Input = styled.textarea`
  flex: 1;
  border: none;
  resize: none;
  outline: none;
  background: transparent;
  font-size: ${({ theme }) => theme.typography.body.fontSize};
  color: ${({ theme }) => theme.colors.textPrimary};
  padding: ${({ theme }) => theme.spacing.xs};
  min-height: 36px;
  max-height: 120px;
  overflow-y: auto;
`;

const SendButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.primary};
  transition: ${({ theme }) => theme.transitions.fast};
  margin-left: ${({ theme }) => theme.spacing.sm};

  &:hover {
    color: ${({ theme }) => theme.colors.primaryHover};
  }
`;

export const ChatInputBox: React.FC<ChatInputBoxProps> = ({ value, onChange, onSend }) => {
  const handleSend = () => {
    if (value.trim()) {
      onSend(value);
      onChange(""); // clear input
    }
  };

  return (
    <Container>
      <Input
        rows={1}
        placeholder="Type a message..."
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            handleSend();
          }
        }}
      />
      <SendButton onClick={handleSend}>
        <Send size={20} />
      </SendButton>
    </Container>
  );
};